import { record, eventWithTime } from 'rrweb';
import { v4 as uuidv4 } from 'uuid';
import { FingerprintJS } from '@fingerprintjs/fingerprintjs';
import { generateHumanId } from 'human-id';
import { SessionableOptions, SessionMetrics, SessionPayload } from '../types';

/**
 * Core Sessionable class for session recording
 */
export class Sessionable {
    private options: SessionableOptions;
    private events: eventWithTime[] = [];
    private stopFn: (() => void) | null = null;
    private eventsInterval: number | null = null;
    private metrics: SessionMetrics;
    private userIdentity: {
        userId?: string;
        anonymousId?: string;
        organizationId?: string;
        userAttributes?: Record<string, any>;
    } = {};
    private fpPromise: Promise<any> | null = null;
    private sessionsApiEndpoint: string;
    private identifyApiEndpoint: string;
    private batchSize = 100;
    private batchInterval = 5000;

    private static readonly SESSION_ID_KEY = 'sessionable_session_id';
    private static readonly ANONYMOUS_ID_KEY = 'sessionable_anonymous_id';
    private static readonly PROD_API_BASE = 'https://sessionable.pages.dev';
    private static readonly DEV_API_BASE = 'http://localhost:5173';

    /**
     * Creates a new Sessionable instance
     */
    constructor(options: SessionableOptions) {
        this.options = {
            metadata: {},
            maskInputs: false,
            enableDeviceId: true,
            ...options
        };

        // Set up API endpoints
        const apiBase = this.options.apiEndpoint || 
                        (this.options.devMode ? Sessionable.DEV_API_BASE : Sessionable.PROD_API_BASE);
        
        this.sessionsApiEndpoint = `${apiBase}/api/sessions`;
        this.identifyApiEndpoint = `${apiBase}/api/identify`;

        // Initialize metrics
        this.metrics = {
            sessionId: this.generateReadableId(),
            eventsRecorded: 0,
            batchesSent: 0,
            batchesSuccessful: 0,
            connectionStatus: 'disconnected',
            lastError: null,
            lastBatchSize: 0,
            lastSuccessfulUpload: null,
            recordingStartTime: new Date(),
            isRecording: false
        };

        // Set up existing identity info if provided
        if (options.user) {
            this.userIdentity = { ...options.user };
        }

        // Initialize device ID (anonymous ID) if enabled
        if (this.options.enableDeviceId !== false) {
            this.initializeDeviceId();
        } else {
            // Use a fallback random ID
            this.userIdentity.anonymousId = this.generateFallbackId();
        }

        // Try to restore existing session ID
        try {
            const storedSessionId = localStorage.getItem(Sessionable.SESSION_ID_KEY);
            if (storedSessionId) {
                this.metrics.sessionId = storedSessionId;
            } else {
                localStorage.setItem(Sessionable.SESSION_ID_KEY, this.metrics.sessionId);
            }

            // Store anonymous ID if available
            if (this.userIdentity.anonymousId) {
                localStorage.setItem(Sessionable.ANONYMOUS_ID_KEY, this.userIdentity.anonymousId);
            } else {
                const storedAnonymousId = localStorage.getItem(Sessionable.ANONYMOUS_ID_KEY);
                if (storedAnonymousId) {
                    this.userIdentity.anonymousId = storedAnonymousId;
                } else if (!this.userIdentity.anonymousId) {
                    // If no anonymous ID exists or is stored, generate a fallback
                    this.userIdentity.anonymousId = this.generateFallbackId();
                    localStorage.setItem(Sessionable.ANONYMOUS_ID_KEY, this.userIdentity.anonymousId);
                }
            }
        } catch (e) {
            console.warn('Sessionable: Unable to access localStorage', e);
        }
    }

    /**
     * Generate a consistent human-readable ID based on a hash
     * Creates IDs like "rare-geckos-jam" or "famous-owls-film"
     */
    private generateReadableId(): string {
        return generateHumanId({
            separator: '-',
            capitalize: false
        });
    }

    /**
     * Initialize device ID for anonymous identification
     * Uses browser fingerprinting when enableDeviceId is true,
     * otherwise falls back to random UUID
     */
    private initializeDeviceId(): void {
        try {
            // Import and initialize FingerprintJS
            this.fpPromise = FingerprintJS.load();
            
            this.fpPromise
                .then(fp => fp.get())
                .then(result => {
                    this.userIdentity.anonymousId = result.visitorId;
                    try {
                        localStorage.setItem(Sessionable.ANONYMOUS_ID_KEY, result.visitorId);
                    } catch (e) {
                        console.warn('Sessionable: Unable to store device ID in localStorage', e);
                    }
                })
                .catch(error => {
                    console.warn('Sessionable: Error getting device fingerprint, using fallback ID', error);
                    this.userIdentity.anonymousId = this.generateFallbackId();
                });
        } catch (e) {
            console.warn('Sessionable: Error initializing device ID, using fallback', e);
            this.userIdentity.anonymousId = this.generateFallbackId();
        }
    }

    /**
     * Generate a fallback random ID when fingerprinting is disabled or fails
     */
    private generateFallbackId(): string {
        return uuidv4();
    }

    /**
     * Identify a user for the current session
     *
     * This will store the user identity immediately and include it with all future event batches.
     * If a session is currently active, it will also make an API call to link the user to the session.
     * If no session is active, the identity will be applied when the session starts.
     */
    identify(userId: string, options?: { 
        organizationId?: string;
        userAttributes?: Record<string, any>;
    }): void {
        if (!userId) {
            console.warn('Sessionable: identify() called without a userId');
            return;
        }

        this.userIdentity.userId = userId;
        
        if (options?.organizationId) {
            this.userIdentity.organizationId = options.organizationId;
        }
        
        if (options?.userAttributes) {
            this.userIdentity.userAttributes = {
                ...this.userIdentity.userAttributes,
                ...options.userAttributes
            };
        }

        // If session is active and we have anonymous ID, call identify API
        if (this.metrics.isRecording && this.userIdentity.anonymousId) {
            this.callIdentifyApi();
        }
    }

    /**
     * Make the API call to identify a user with the current session
     * This is separated from the identify method to allow for async handling
     * without blocking the main identify call
     */
    private callIdentifyApi(): void {
        if (!this.userIdentity.anonymousId || !this.userIdentity.userId) {
            // Missing required identification data
            return;
        }

        const payload = {
            anonymous_id: this.userIdentity.anonymousId,
            user_id: this.userIdentity.userId,
            organization_id: this.userIdentity.organizationId,
            user_attributes: this.userIdentity.userAttributes,
            session_id: this.metrics.sessionId
        };

        fetch(this.identifyApiEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.options.apiKey}`
            },
            body: JSON.stringify(payload)
        }).catch(error => {
            console.warn('Sessionable: Error calling identify API', error);
        });
    }

    /**
     * Get the current anonymous ID
     */
    getAnonymousId(): string | undefined {
        return this.userIdentity.anonymousId;
    }

    /**
     * Start recording the session
     */
    async start(): Promise<void> {
        if (this.stopFn) {
            console.warn('Sessionable: Session recording already in progress');
            return;
        }

        // If fingerprinting is enabled but we don't have an anonymous ID yet,
        // wait for the fingerprinting to complete
        if (this.options.enableDeviceId && this.fpPromise && !this.userIdentity.anonymousId) {
            try {
                const fp = await this.fpPromise;
                const result = await fp.get();
                this.userIdentity.anonymousId = result.visitorId;
                try {
                    localStorage.setItem(Sessionable.ANONYMOUS_ID_KEY, result.visitorId);
                } catch (e) {
                    console.warn('Sessionable: Unable to store device ID in localStorage', e);
                }
            } catch (error) {
                console.warn('Sessionable: Error getting device fingerprint on start, using fallback ID', error);
                this.userIdentity.anonymousId = this.generateFallbackId();
            }
        }

        // Make sure we have an anonymous ID
        if (!this.userIdentity.anonymousId) {
            this.userIdentity.anonymousId = this.generateFallbackId();
            try {
                localStorage.setItem(Sessionable.ANONYMOUS_ID_KEY, this.userIdentity.anonymousId);
            } catch (e) {
                console.warn('Sessionable: Unable to store anonymous ID in localStorage', e);
            }
        }

        // Update metrics for recording start
        this.metrics.recordingStartTime = new Date();
        this.metrics.isRecording = true;

        // Start rrweb recording
        this.stopFn = record({
            emit: (event) => {
                this.events.push(event);
                this.updateMetrics();
            },
            maskInputs: this.options.maskInputs,
            maskTextSelector: this.options.maskTextSelector,
        });

        // Set up periodic events sending
        this.eventsInterval = window.setInterval(() => {
            if (this.events.length > 0) {
                this.sendEvents();
            }
        }, this.batchInterval);

        // Immediately call identify API if we already have user identity
        if (this.userIdentity.userId && this.userIdentity.anonymousId) {
            this.callIdentifyApi();
        }
    }

    /**
     * Stop recording the session
     */
    stop(): void {
        if (this.stopFn) {
            this.stopFn();
            this.stopFn = null;
        }

        if (this.eventsInterval) {
            clearInterval(this.eventsInterval);
            this.eventsInterval = null;
        }

        // Send any remaining events
        if (this.events.length > 0) {
            this.sendEvents();
        }

        this.metrics.isRecording = false;
    }

    /**
     * Get current session metrics
     */
    getMetrics(): SessionMetrics {
        return { ...this.metrics };
    }

    /**
     * Add custom metadata to the session
     */
    addMetadata(metadata: Record<string, any>): void {
        this.options.metadata = {
            ...this.options.metadata,
            ...metadata
        };
    }

    /**
     * Send events to the Sessionable API
     */
    private sendEvents(): void {
        if (this.events.length === 0) return;

        this.metrics.connectionStatus = 'connecting';
        const eventsToSend = this.events.slice(0, this.batchSize);
        this.events = this.events.slice(this.batchSize);

        const payload: SessionPayload = {
            session_id: this.metrics.sessionId,
            events: eventsToSend,
            metadata: this.options.metadata || {},
            anonymous_id: this.userIdentity.anonymousId || '',
            user_id: this.userIdentity.userId,
            organization_id: this.userIdentity.organizationId,
            user_attributes: this.userIdentity.userAttributes
        };

        this.metrics.batchesSent++;
        this.metrics.lastBatchSize = eventsToSend.length;

        fetch(this.sessionsApiEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${this.options.apiKey}`
            },
            body: JSON.stringify(payload)
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`API responded with status ${response.status}`);
                }
                this.metrics.batchesSuccessful++;
                this.metrics.lastSuccessfulUpload = new Date();
                this.metrics.connectionStatus = 'connected';
                this.metrics.lastError = null;
            })
            .catch(error => {
                this.metrics.connectionStatus = 'error';
                this.metrics.lastError = error.message;
                console.error('Sessionable: Error sending events', error);
            });
    }

    /**
     * Update session metrics
     */
    private updateMetrics(): void {
        this.metrics.eventsRecorded++;
    }
}

export const VERSION = "0.2.1";  // Updated from the npm package version